import { csCZ as coreCsCZ } from '@mui/material/locale';
import { createTheme } from '@mui/material/styles';
import { csCZ as DatagridCsCZ } from '@mui/x-data-grid/locales';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { csCZ } from '@mui/x-date-pickers/locales';
import { RADIUS_CONTROL, RADIUS_SURFACE, SURFACE_BORDER_COLOR, SURFACE_SHADOW } from './tokens';

export const RPSTheme = createTheme(
  {
    palette: {
      background: {
        default: '#F6F7F9',
        paper: '#ffffff',
        header: '#C62327'
      },
      primary: {
        main: '#C62327'
      },
      secondary: {
        main: '#242424'
      },
      text: {
        primary: '#222234'
      },
      success: {
        main: '#1E8A3C',
        light: '#3DBF00'
      },
      info: {
        main: '#1F6FB5'
      },
      divider: 'rgba(34, 34, 52, 0.08)',
      warning: {
        main: '#FCA311'
      }
    },
    typography: {
      fontFamily: 'Wix Madefor Text, sans-serif',
      body0: {
        fontSize: '1.25rem',
        lineHeight: 1.333333333333333
      },
      body3: {
        fontSize: '0.775rem',
        lineHeight: 1
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.01em'
      }
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          rounded: ({ theme }) => ({ borderRadius: theme.shape.borderRadius * RADIUS_SURFACE })
        }
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({ borderRadius: theme.shape.borderRadius * RADIUS_CONTROL })
        }
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            borderRadius: '4px',
            padding: 0,
            fontSize: '0.7rem',
            minWidth: '18px',
            height: '18px'
          }
        }
      },
      MuiButton: {
        defaultProps: {
          disableRipple: true,
          disableElevation: true,
          sx: {
            textTransform: 'none',
            fontWeight: 500
          }
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius * RADIUS_CONTROL,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover, &:active, &.Mui-focusVisible': { boxShadow: 'none' },
            '&:active': { transform: 'scale(0.98)' },
            '@media (prefers-reduced-motion: no-preference)': {
              transition: 'background-color 0.2s, border-color 0.2s, transform 0.15s'
            }
          }),
          sizeLarge: {
            padding: '12px 38px'
          }
        }
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: '0.9rem'
          }
        }
      },
      MuiToggleButton: {
        defaultProps: {
          disableRipple: true,
          sx: {
            textTransform: 'none',
            fontWeight: 500,
            border: 'none',
            color: 'text.primary'
          }
        },
        styleOverrides: {
          root: ({ theme }) => ({ borderRadius: theme.shape.borderRadius * RADIUS_CONTROL }),
          sizeMedium: {
            padding: '6px 16px'
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            padding: '10px',
            border: `1px solid ${SURFACE_BORDER_COLOR}`,
            boxShadow: SURFACE_SHADOW
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius * RADIUS_CONTROL,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34, 34, 52, 0.18)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34, 34, 52, 0.4)' }
          })
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ theme }) => ({ borderRadius: theme.shape.borderRadius * RADIUS_CONTROL })
        }
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: 'rgba(34, 34, 52, 0.08)' } }
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: 'rgba(34, 34, 52, 0.08)' },
          head: { fontWeight: 600, color: '#5E5E70', backgroundColor: '#FAFAFC' }
        }
      },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius * RADIUS_SURFACE,
            borderColor: SURFACE_BORDER_COLOR,
            backgroundColor: theme.palette.background.paper,
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#FAFAFC' },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(198, 35, 39, 0.04)' },
            '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeaders': { borderColor: 'rgba(34, 34, 52, 0.08)' }
          })
        }
      },
      MuiFormLabel: {
        defaultProps: {
          sx: {
            color: 'text.primary',
            fontWeight: '600'
          }
        }
      },
      MuiInputBase: {
        defaultProps: {
          sx: {
            color: 'text.primary',
            backgroundColor: '#F8F8FA'
          }
        }
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontSize: '.9rem',
            fontWeight: 'bold'
          }
        },
        defaultProps: {
          StepIconProps: {
            sx: {
              width: '2rem',
              height: '2rem'
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            //background: '#fff',
            //padding: '0 10px'
          }
        }
      }
    }
  },
  csCZ,
  DatagridCsCZ,
  coreCsCZ
);
